import type { INovaDock, INovaSchedule, INovaWarehouse } from '@/interfaces/nova';
import type { NovaApiService } from '@/services/nova/NovaApiService';
import { parseDockJsonColumns } from '@/services/importHelpers';
import { cleanWhiteSpace } from '@/services/util';
import {
  ALREADY_EXISTS_MESSAGE,
  SUCCESS_IMPORT_MESSAGE,
  forEachSequentially,
  getFailureStatusMessage,
  removeEmptyProperties,
  wasAlreadyImported
} from './importShared';

type DockImportParams = {
  orgId: string;
  docks: INovaDock[];
  apiService: NovaApiService;
  loadTypeIdsKeyedByExternalId: { [key: string]: string };
  warehousesKeyedByExternalId: { [key: string]: INovaWarehouse };
};

type DockImportState = Omit<DockImportParams, 'docks'> & {
  existingDocks: INovaDock[];
  docksKeyedByExternalId: { [key: string]: INovaDock };
  docksReport: INovaDock[];
};

export async function updateMaptsDockSchedule(
  apiService: NovaApiService,
  dock: INovaDock,
  schedule: INovaSchedule
) {
  await apiService.updateDock(dock.id as string, {
    schedule
  });
}

function reportDockFailure(dock: INovaDock, message: string, { docksReport }: DockImportState) {
  dock.wasImportedIntoNova = getFailureStatusMessage({ message });
  dock.schedule = JSON.stringify(dock.schedule);
  docksReport.unshift(dock);
}

async function createDock(
  dock: INovaDock,
  warehouseId: string,
  { orgId, apiService, docksKeyedByExternalId, docksReport }: DockImportState
) {
  try {
    dock.warehouseId = warehouseId;
    dock.orgId = orgId;
    const originalSchedule = dock.schedule;
    // Docks should be created with an open schedule to accommodate booking outside of operating hours
    // Using this instead of null - it saves as "null" in the DB and that makes me nervous
    dock.schedule = JSON.parse('{"version": 1}');
    const response = await apiService.createDock(dock);
    const [newDock] = response.data;
    if (!newDock) {
      throw new Error('no dock was returned');
    }
    dock.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;

    // Set schedule back to original so we can update after appointments are created
    dock.schedule = JSON.stringify(originalSchedule);

    if (dock.externalId && newDock.id) {
      docksKeyedByExternalId[dock.externalId] = newDock;
      dock.id = newDock.id;
    }
    docksReport.push(dock);
  } catch (e) {
    dock.wasImportedIntoNova = getFailureStatusMessage({ e });
    dock.schedule = JSON.stringify(dock.schedule);
    docksReport.unshift(dock);
  }
}

async function importDock(dock: INovaDock, importState: DockImportState) {
  const { loadTypeIdsKeyedByExternalId, warehousesKeyedByExternalId, existingDocks } = importState;
  const { docksKeyedByExternalId, docksReport } = importState;
  dock.name = cleanWhiteSpace(dock.name);
  if (wasAlreadyImported(dock) && dock.id) {
    dock.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
    docksKeyedByExternalId[dock.externalId] = dock;
    docksReport.push(dock);
    return;
  }
  // TODO: Seeing pattern with checking existing entities here - explore abstraction
  const dockWarehouseId = warehousesKeyedByExternalId[dock.externalWarehouseId]?.id;
  const existingDock = existingDocks.find(
    existing =>
      dock.name.toLowerCase() === cleanWhiteSpace(existing.name).toLowerCase() &&
      dockWarehouseId === existing.warehouseId
  );
  if (existingDock) {
    dock.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
    dock.id = existingDock.id;
    docksReport.push(dock);
    if (dock.externalId && existingDock.id) {
      docksKeyedByExternalId[dock.externalId] = existingDock;
    }
    return;
  }
  dock.allowOverBooking = dock.isMapts || false;
  const { schedule, originalSchedule, loadTypeIds, failureMessage } = parseDockJsonColumns(
    dock,
    loadTypeIdsKeyedByExternalId
  );
  dock.schedule = schedule;
  if (failureMessage) {
    dock.wasImportedIntoNova = getFailureStatusMessage({ message: failureMessage });
    dock.schedule = originalSchedule;
    importState.docksReport.unshift(dock);
    return;
  }
  dock.loadTypeIds = loadTypeIds;
  removeEmptyProperties(dock);
  const warehouseId = warehousesKeyedByExternalId[dock.externalWarehouseId]?.id as string;
  if (!warehouseId) {
    reportDockFailure(
      dock,
      `Dock's Warehouse (${dock.externalWarehouseId}) failed to import - see Warehouses tab for details`,
      importState
    );
    return;
  }
  await createDock(dock, warehouseId, importState);
}

export async function importDocks({
  orgId,
  docks,
  apiService,
  loadTypeIdsKeyedByExternalId,
  warehousesKeyedByExternalId
}: DockImportParams) {
  const existingDocksRes = await apiService.getDocks();
  const importState: DockImportState = {
    orgId,
    apiService,
    loadTypeIdsKeyedByExternalId,
    warehousesKeyedByExternalId,
    existingDocks: existingDocksRes.data ?? [],
    docksKeyedByExternalId: {},
    docksReport: []
  };

  await forEachSequentially(docks, dock => importDock(dock, importState));

  return {
    docksKeyedByExternalId: importState.docksKeyedByExternalId,
    docksReport: importState.docksReport
  };
}
